// src/quiz/gre-mock7/verbalData.js
// GRE Mock 7 — Verbal Reasoning.
// Section 1: questions 1–12 (18 min). Section 2: questions 13–27 (23 min).
// Question format: see ../greQuestionTypes.js

const TIDAL_PASSAGE =
  "Coastal engineers have long viewed tidal power as an attractive but impractical energy source. Unlike wind or solar generation, tides follow a predictable schedule, which allows grid operators to plan around them. Yet the high cost of building turbines that can withstand corrosive seawater and powerful currents has kept most projects at the pilot stage. Recent designs, however, place small turbines in clusters on the seabed, where they are cheaper to install and easier to service than a single large barrage. Skeptics note that the environmental effects of such clusters on fish migration remain poorly understood, and they urge regulators to require long-term monitoring before any large-scale deployment.";

const TRANSLATION_PASSAGE =
  "Translators are often praised when their work is invisible, that is, when a reader forgets that the text was ever written in another language. Recent scholars challenge this ideal, arguing that every translation is an act of interpretation: decisions about idiom, rhythm and tone inevitably reshape the original. A translation that reads fluently in the target language may in fact conceal how much has been altered. Some translators now add prefaces explaining their choices, inviting readers to see translation as a creative act rather than a mechanical transfer of meaning.";

const verbalQuestions = [

  // ---------- SECTION 1 ----------

  {
    id: 1,
    question: "The dean's remarks, though intended to be ______, only deepened the faculty's resentment.",
    options: ["conciliatory", "provocative", "tedious", "brief"],
    answer: "conciliatory",
    explanation: "\"Though intended\" sets up a result opposite to the goal. Remarks that deepened resentment were meant to soothe: conciliatory. Provocative remarks would have succeeded, not backfired.",
  },
  {
    id: 2,
    question: "Because the explorer's account was ______ by later evidence, historians now treat it with suspicion.",
    options: ["corroborated", "undermined", "extended", "replicated"],
    answer: "undermined",
    explanation: "Historians are suspicious because later evidence weakened the account: undermined. Corroborated (confirmed) would earn the account trust, not suspicion.",
  },
  {
    id: 3,
    question: "The playwright's early work was marked by ______; her later plays, by contrast, showed remarkable restraint.",
    options: ["extravagance", "austerity", "modesty", "subtlety"],
    answer: "extravagance",
    explanation: "\"By contrast\" with restraint calls for excess: extravagance. Austerity, modesty and subtlety are all forms of restraint.",
  },
  {
    id: 4,
    question: "Her ______ refusal to compromise, even when the evidence demanded it, ultimately cost her the support of her allies.",
    options: ["flexible", "obdurate", "tentative", "amiable"],
    answer: "obdurate",
    explanation: "Refusing to compromise even against the evidence is stubborn: obdurate. A flexible or tentative refusal is a contradiction.",
  },
  {
    id: 5,
    question: "Though the novel received only ______ reviews on its release, it has since come to be regarded as a masterpiece.",
    options: ["laudatory", "tepid", "glowing", "rapturous"],
    answer: "tepid",
    explanation: "\"Though … only\" contrasts the early reviews with later acclaim, so they were lukewarm: tepid. Laudatory, glowing and rapturous all mean strong praise.",
  },
  {
    id: 6,
    question: "The new software is remarkably ______: even first-time users can complete complex tasks within minutes.",
    options: ["intuitive", "cumbersome", "convoluted", "arcane"],
    answer: "intuitive",
    explanation: "The colon explains the blank: if first-time users succeed quickly, the software is easy to understand—intuitive. The other choices describe software that is hard to use.",
  },
  {
    id: 7,
    question: "The treaty was largely ______, since neither side had any intention of honoring its terms.",
    options: ["binding", "nominal", "comprehensive", "enforceable"],
    answer: "nominal",
    explanation: "A treaty nobody intends to honor exists in name only: nominal. Binding and enforceable say the opposite.",
  },
  {
    id: 8,
    type: "blanks",
    question: "The senator's speech was (i)______ in tone but (ii)______ in substance, offering no concrete proposals.",
    blanks: [
      { options: ["forceful", "hesitant", "muted"] },
      { options: ["vacuous", "substantive", "rigorous"] },
    ],
    answer: ["forceful", "vacuous"],
    explanation: "\"Offering no concrete proposals\" means the substance was empty: vacuous. \"But\" requires the tone to contrast with that weakness, so it was strong: forceful. A hesitant or muted tone would not contrast with empty content.",
  },
  {
    id: 9,
    type: "blanks",
    question: "Although the data seemed (i)______ at first, further analysis revealed a (ii)______ pattern that explained nearly every fluctuation.",
    blanks: [
      { options: ["random", "predictable", "conclusive"] },
      { options: ["coherent", "chaotic", "fleeting"] },
    ],
    answer: ["random", "coherent"],
    explanation: "A pattern that explains nearly every fluctuation is coherent. \"Although\" requires the first impression to be the opposite: the data seemed random. Predictable data would not contrast with finding a pattern.",
  },
  {
    id: 10,
    question: "The witness's testimony was ______ by contradictions, leaving the jury unsure whom to believe.",
    options: ["bolstered", "clarified", "marred", "confirmed"],
    answer: "marred",
    explanation: "Contradictions that leave the jury unsure have damaged the testimony: marred. The other choices would strengthen it.",
  },
  {
    id: 11,
    question: "Her ______ attention to detail made her an exceptional editor, catching errors that others overlooked.",
    options: ["cursory", "scrupulous", "sporadic", "casual"],
    answer: "scrupulous",
    explanation: "Catching errors others miss takes extremely careful attention: scrupulous. Cursory, sporadic and casual attention would miss errors.",
  },
  {
    id: 12,
    question: "The committee's decision to postpone the vote was seen as an ______ tactic designed to avoid an unpopular outcome.",
    options: ["forthright", "evasive", "decisive", "courageous"],
    answer: "evasive",
    explanation: "Postponing a vote to dodge an unpopular result is a way of avoiding the issue: evasive. Forthright, decisive and courageous all suggest facing it.",
  },

  // ---------- SECTION 2 ----------

  {
    id: 13,
    question: "Critics charged that the film's plot was so ______ that viewers could predict every twist.",
    options: ["original", "formulaic", "enigmatic", "intricate"],
    answer: "formulaic",
    explanation: "A plot whose every twist is predictable follows a familiar pattern: formulaic. Original, enigmatic and intricate plots would be harder to predict.",
  },
  {
    id: 14,
    question: "In contrast to the ______ of his early career, his later years were marked by quiet stability.",
    options: ["turbulence", "serenity", "routine", "calm"],
    answer: "turbulence",
    explanation: "\"In contrast to\" quiet stability calls for disorder: turbulence. Serenity, routine and calm would agree with stability.",
  },
  {
    id: 15,
    question: "The chemist's findings, rather than ______ the established theory, lent it further support.",
    options: ["challenging", "confirming", "supporting", "extending"],
    answer: "challenging",
    explanation: "\"Rather than\" contrasts the blank with lending support, so the blank is the opposite: challenging. Confirming and supporting would repeat the second half instead of contrasting with it.",
  },
  {
    id: 16,
    type: "blanks",
    question: "The mayor was widely (i)______ for his (ii)______ handling of the flood, which saved many lives.",
    blanks: [
      { options: ["praised", "criticized", "ignored"] },
      { options: ["prompt", "negligent", "belated"] },
    ],
    answer: ["praised", "prompt"],
    explanation: "Handling that saved many lives was quick and effective: prompt. Such handling earns praise, so the mayor was praised. Negligent or belated handling would not have saved lives.",
  },
  {
    id: 17,
    passage: TIDAL_PASSAGE,
    question: "The passage is primarily concerned with",
    options: [
      "arguing that tidal power should replace all other energy sources",
      "describing the promise of tidal power, the obstacles it faces, and a concern that remains unresolved",
      "proving that turbines harm fish populations",
      "comparing wind and solar generation in detail",
    ],
    answer: "describing the promise of tidal power, the obstacles it faces, and a concern that remains unresolved",
    explanation: "The passage covers tidal power's advantage (predictability), its obstacle (cost), a new design that may help, and the skeptics' unresolved environmental concern. Only B covers all of that.",
  },
  {
    id: 18,
    passage: TIDAL_PASSAGE,
    question: "According to the passage, one advantage of tidal power over wind or solar generation is that it",
    options: [
      "is cheaper to build",
      "follows a predictable schedule",
      "requires no turbines",
      "has no environmental effects",
    ],
    answer: "follows a predictable schedule",
    explanation: "The passage states that \"unlike wind or solar generation, tides follow a predictable schedule.\" Cost is described as tidal power's main obstacle, not an advantage.",
  },
  {
    id: 19,
    passage: TIDAL_PASSAGE,
    question: "According to the passage, most tidal projects have remained at the pilot stage mainly because",
    options: [
      "tides are unpredictable",
      "regulators have banned them",
      "durable turbines are expensive to build",
      "fish migration has stopped",
    ],
    answer: "durable turbines are expensive to build",
    explanation: "The passage says \"the high cost of building turbines that can withstand corrosive seawater and powerful currents has kept most projects at the pilot stage.\"",
  },
  {
    id: 20,
    passage: TIDAL_PASSAGE,
    question: "The skeptics mentioned in the passage would most likely favor",
    options: [
      "immediate large-scale deployment of seabed clusters",
      "long-term monitoring before large-scale deployment",
      "abandoning tidal research entirely",
      "replacing clusters with a single large barrage",
    ],
    answer: "long-term monitoring before large-scale deployment",
    explanation: "The skeptics \"urge regulators to require long-term monitoring before any large-scale deployment.\" They raise a concern but don't call for abandoning the technology.",
  },
  {
    id: 21,
    passage: TIDAL_PASSAGE,
    question: "The author mentions seabed turbine clusters primarily to",
    options: [
      "present a development that may ease the cost obstacle",
      "prove that large barrages are superior",
      "show that tidal power is unreliable",
      "criticize regulators",
    ],
    answer: "present a development that may ease the cost obstacle",
    explanation: "Right after naming cost as the main obstacle, the passage introduces clusters as \"cheaper to install and easier to service\"—a possible answer to that obstacle.",
  },
  {
    id: 22,
    question: "The benefactor's ______ was evident in her habit of donating anonymously and never seeking recognition.",
    options: ["vanity", "altruism", "ambition", "frugality"],
    answer: "altruism",
    explanation: "Giving anonymously without seeking credit shows selfless concern for others: altruism. Vanity and ambition would seek recognition; frugality conflicts with donating.",
  },
  {
    id: 23,
    question: "Although the proposal was widely ______, the board approved it after only minor revisions.",
    options: ["criticized", "praised", "ignored", "welcomed"],
    answer: "criticized",
    explanation: "\"Although\" signals that approval after only minor changes was surprising, so the proposal had been criticized. Praised and welcomed would make approval expected.",
  },
  {
    id: 24,
    question: "The scientist's cautious conclusions stood in sharp ______ to the sensational headlines about her work.",
    options: ["harmony", "contrast", "proportion", "agreement"],
    answer: "contrast",
    explanation: "Cautious conclusions and sensational headlines are opposites, and \"stood in sharp ___ to\" completes the idiom: contrast.",
  },
  {
    id: 25,
    passage: TRANSLATION_PASSAGE,
    question: "The passage is primarily concerned with",
    options: [
      "praising translators who remain invisible",
      "arguing that translation involves interpretation, contrary to a common ideal",
      "explaining how to learn a foreign language",
      "criticizing readers who prefer fluent texts",
    ],
    answer: "arguing that translation involves interpretation, contrary to a common ideal",
    explanation: "The passage presents the ideal of the invisible translator, then reports scholars' argument that every translation is an interpretation. That challenge is its main point.",
  },
  {
    id: 26,
    passage: TRANSLATION_PASSAGE,
    question: "The author suggests that a fluent translation may",
    options: [
      "be impossible to produce",
      "hide how much the original has been changed",
      "always be more accurate than the original",
      "be disliked by all readers",
    ],
    answer: "hide how much the original has been changed",
    explanation: "The passage says a fluent translation \"may in fact conceal how much has been altered.\"",
  },
  {
    id: 27,
    passage: TRANSLATION_PASSAGE,
    question: "The translators' prefaces mentioned in the passage most likely reflect a desire to",
    options: [
      "avoid responsibility for errors",
      "shorten the translated text",
      "help readers see translation as a creative act",
      "imitate the original author's style exactly",
    ],
    answer: "help readers see translation as a creative act",
    explanation: "The prefaces explain translators' choices, \"inviting readers to see translation as a creative act rather than a mechanical transfer of meaning.\"",
  },
];

export default verbalQuestions;
